/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Define the few assistant controls a teacher changes mid-lesson, and their scope.
 * @state-owner live policy store holds the active overrides
 * @allowed-imports none
 * @forbidden React; network; store implementations
 * @public-api live module
 * @side-effects none
 * @tests modules/live/model/agent-policy.test.ts
 */

/**
 * Six controls, not a prompt box. Each one maps to a decision a teacher actually makes during a
 * lesson, and each is compiled into the assistant's instructions rather than typed by hand.
 */
export interface AgentPolicy {
  /** How far a hint may go before it becomes the answer. */
  readonly hintDepth: 'minimal' | 'guided' | 'worked';
  /** Make the student show an attempt before the assistant says anything useful. */
  readonly attemptFirst: boolean;
  readonly fullSolution: 'never' | 'after-attempts' | 'allow';
  readonly turnLength: 'short' | 'normal' | 'long';
  /** Whether the assistant waits to be asked, nudges, or opens conversations. */
  readonly proactivity: 'silent' | 'onDemand' | 'active';
  /** Where its explanations may come from; the class may use a different definition. */
  readonly sources: 'teacherOnly' | 'teacherPlus' | 'open';
}

export type PolicyScope = 'class' | 'group' | 'student';

export interface PolicyOverride {
  readonly id: string;
  readonly scope: PolicyScope;
  /** Student names for `student`/`group`; empty for `class`. */
  readonly targets: readonly string[];
  readonly patch: Partial<AgentPolicy>;
  /** Minutes from `at`; an override that never expires is one nobody remembers setting. */
  readonly minutes: number;
  readonly by: string;
  readonly reason: string;
  readonly at: string;
}

export const DEFAULT_POLICY: AgentPolicy = {
  hintDepth: 'guided',
  attemptFirst: true,
  fullSolution: 'never',
  turnLength: 'short',
  proactivity: 'onDemand',
  sources: 'teacherPlus',
};

export const HINT_DEPTH_LABEL = {
  minimal: '只給方向',
  guided: '一步一步問',
  worked: '示範類似題',
} as const;

export const FULL_SOLUTION_LABEL = {
  never: '永遠不給',
  'after-attempts': '嘗試後才給',
  allow: '可以給',
} as const;

export const TURN_LENGTH_LABEL = {
  short: '一兩句',
  normal: '適中',
  long: '可以講長一點',
} as const;

export const PROACTIVITY_LABEL = {
  silent: '不主動說話',
  onDemand: '學生問才回',
  active: '主動關心',
} as const;

export const SOURCES_LABEL = {
  teacherOnly: '只用老師教材',
  teacherPlus: '教材為主',
  open: '不限來源',
} as const;

export const SCOPE_LABEL = { class: '全班', group: '小組', student: '個別學生' } as const;

/**
 * Fixed by the platform, not by each teacher. Leaving no-direct-answer, privacy and safety to
 * whoever writes the best prompt is how they end up missing from the class that needed them most.
 */
export const PLATFORM_GUARDRAILS: readonly string[] = [
  '不直接給出考題的最終答案，無論老師怎麼設定。',
  '不索取或轉述學生的個人聯絡方式。',
  '遇到自傷、霸凌或求助訊號時，立即通知老師並停止一般回覆。',
];

/** Later overrides win, and a narrower scope beats a broader one at the same time. */
const SCOPE_RANK: Readonly<Record<PolicyScope, number>> = { class: 0, group: 1, student: 2 };

export function resolvePolicy(
  overrides: readonly PolicyOverride[],
  student?: string,
): { policy: AgentPolicy; applied: readonly PolicyOverride[] } {
  const applied = overrides
    .filter((override) => {
      if (override.scope === 'class') return true;
      return student ? override.targets.includes(student) : false;
    })
    .sort((a, b) => SCOPE_RANK[a.scope] - SCOPE_RANK[b.scope]);

  // One fresh object collects every patch in order, so no existing policy is ever mutated and
  // the merge stays linear in the number of overrides.
  const policy: AgentPolicy = Object.assign({}, DEFAULT_POLICY, ...applied.map((o) => o.patch));
  return { policy, applied };
}

/** One line a student can read, so a mode change is never invisible to the person it affects. */
export function policyNotice(policy: AgentPolicy): string | null {
  if (policy.proactivity === 'silent') return '老師暫時把助教轉為安靜模式';
  if (policy.attemptFirst && policy.hintDepth === 'minimal') {
    return '老師要你先寫一次，助教只會給方向';
  }
  if (policy.fullSolution === 'allow') return '老師開放助教講完整解法';
  if (policy.sources === 'teacherOnly') return '助教只會用老師的教材回答';
  return null;
}
